<template>
  <div v-if="!isLoading && options.length > 1" class="flex justify-end mx-auto px-2 sm:px-4 lg:px-8 py-2 font-proxima">
    <div class="grid sm:flex sm:gap-4">
      <div class="w-full sm:min-w-[300px]">

        <FormKit
          type="autocomplete"
          name="locations"
          label=""
          outer-class="!mb-0"
          selection-class="pl-2"
          :deselect="false"
          :options="options"
          v-model="location"
          :disabled="loadingChangeLocation"
          :filter="customFilter"
          clear-search-on-open
          open-on-focus
          open-on-click
          popover
          selection-appearance="option"
          inner-class="h-[60px]"
          input-class="h-[38px]"
        >
          <template #option="{ option }">
            <div class="py-2 !font-proxima !text-base">
              <b>{{ option.locationName }}</b>
              <p>{{ option.licenseTypeDescription !== '' ? option.licenseTypeDescription : `Type ${option.type}` }}</p>
            </div>
          </template>
        </FormKit>
        <!--<template v-if="location === '' || location === undefined">
        </template>
        <template v-else>
          <div class="py-2 !font-proxima !text-base px-2 rounded-md border border-gray-300">
            <b>{{ options.find(_ => _.value === location).locationName }}</b>
            <p>{{ options.find(_ => _.value === location).licenseTypeDescription }}</p>
            <button @click="location=''" type="button" class="text-blue-500 text-sm">Change Location</button>
          </div>
        </template>-->
        <div v-if="loadingChangeLocation">
          <p class="text-sm text-red-500">Changing location, please wait...</p>
        </div>
      </div>
    </div>
  </div>
  <div v-if="!isLoading && options.length <= 1" class="pb-8 lg:pb-10"></div>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { useRuntimeConfig } from '#imports';
import { computed, ref, watch, onMounted } from 'vue';
import {useAdminStore} from "~/composables/stores/AdminStore";

const userStore = useUserStore();
const useSession = useSessionStore();
const config = useRuntimeConfig();

// Wait for userStore to be initialized before running the query
const isUserReady = computed(() => !!userStore.currentUser?.profile?.userId);

// Define the query
const { data, isLoading, refetch } = useQuery({
  queryKey: ['user-locations', userStore.currentUser?.profile?.userId],
  queryFn: async () => {
    if (!userStore.currentUser?.profile?.userId) {
      throw new Error('User not initialized');
    }
    return userStore.getUserLocations();
  },
  enabled: isUserReady, // Only run query when user is ready
  staleTime: 1000 * 60 * 5, // Cache for 5 minutes
});

// Compute options from query data
const options = computed(() => {
  if (!data.value?.data) return [];
  return data.value.data
    .map((location: any) => ({
      label: location.locationName,
      value: `${location.locationId}-${location.licensedLocationId}-${location.licenseTypeId}-${location.roleId}`,
      type: parseInt(location.licenseTypeId),
      roleId: location.roleId,
      apptDesc: location.apptDesc,
      locationName: location.locationName,
      licenseTypeDescription: location.licenseTypeDescription,
    }))
    .sort((a: any, b: any) => {
      if (a.locationName < b.locationName) return -1;
      if (a.locationName > b.locationName) return 1;
      return a.type - b.type;
    });
});

const location = ref(null);
const loadingChangeLocation = ref(false);
if (userStore.currentUser?.licensedLocationId) {
  location.value = `${userStore.currentUser?.profile?.locationId}-${userStore.currentUser?.licensedLocationId}-${userStore.currentUser?.licenseTypeId}-${userStore.currentUser?.profile?.roleId}`;
}
watch(
  () => userStore.currentUser?.licensedLocationId,
  (newLocation) => {
    if (newLocation) {
      location.value = `${userStore.currentUser?.profile?.locationId}-${userStore.currentUser?.licensedLocationId}-${userStore.currentUser?.licenseTypeId}-${userStore.currentUser?.profile?.roleId}`;
    }
  },
  { immediate: true }
);

const channel = new BroadcastChannel('location_change');
// Watch for location changes
const adminStore = useAdminStore();
watch(location, async (newValue: any, oldValue: any) => {
  adminStore.notifChecked = false;
  if (newValue === undefined) {
    return;
  }
  if (oldValue === null) {
    return;
  } // Skip if oldValue is null
  if (newValue === oldValue) return;

  loadingChangeLocation.value = true;
  const [locationId, licensedLocationId, licenseTypeId, roleId] = location.value.split('-');
  const locationData = data.value?.data?.find(
    (loc: any) =>
      loc.locationId === parseInt(locationId) &&
      loc.licensedLocationId === parseInt(licensedLocationId) &&
      loc.licenseTypeId === parseInt(licenseTypeId) &&
      String(loc.roleId) === roleId
  );

  try {
    const response = await $fetch(`${config.public.legacyApiBase}/legacy/auth/change-location`, {
      method: 'post',
      ...globalFetchOptions(),
      body: {
        userId: userStore.currentUser.profile?.userId,
        moduleId: locationData?.moduleId,
        locationId: locationData?.locationId,
        licensedLocationId: locationData?.licensedLocationId,
        roleId: locationData?.roleId,
        licenseTypeId: locationData?.licenseTypeId,
        adminUserId: userStore.currentUser.adminUserId,
      },
    });

    setUserSession(`${response.data.token}`);
    useSession.resetTimer();
    useSession.startCountdownTimer();
    await userStore.getCurrentUser();

    sessionStorage.setItem('userUpdate', 'yes');
    channel.postMessage({ type: 'location_update', data: 'login' });
    window.location.href = '/';
  } catch (error) {
    toastError('Error changing location', error?.response?._data?.error.message);
  } finally {
    loadingChangeLocation.value = false;
  }
});

// Ensure query runs on client-side after user initialization
onMounted(async () => {
  if (!userStore.currentUser?.profile?.userId) {
    await userStore.getCurrentUser(); // Fetch user if not initialized
  }
  if (isUserReady.value && (!data.value || options.value.length === 0)) {
    refetch(); // Re-run query if no data
  }
});



const customFilter = (option: any, search: string) => {
  if (!search) return true;
  return (
      option.locationName.toLowerCase().includes(search.toLowerCase()) ||
      option.licenseTypeDescription.toLowerCase().includes(search.toLowerCase())
  );
}
</script>
